// covered-by: frontends/carrie/src/plugins/__tests__/rehype-claim-citation.test.ts

/**
 * Turns the claim a review comment QUOTES into the link to the rule it rests on.
 *
 * The citation was a chip under the comment, so a reader had to match prose
 * against a chip themselves -- and in the ask-v2 review pane the chip does not
 * render at all, which leaves a finding asserting a registered claim with no
 * way to see it. The claim wording is already in the sentence; making that the
 * link is the smallest thing that can be clicked.
 *
 * why a rehype plugin and not a string replace before render: the body is
 * markdown, and a link spliced into the SOURCE would have to survive being
 * parsed -- a claim containing `*`, `_`, `[` or a backtick would be read as
 * emphasis, or would break the `**Fix:**` bold it usually sits inside. Splicing
 * into the hast AFTER parsing edits the rendered text nodes only, so markdown
 * syntax is already resolved and cannot be disturbed. It is also the pattern
 * this repo already uses for exactly this shape (`rehype-iconize-emoji`,
 * `rehype-color-chip`).
 *
 * why matching is best-effort: nothing guarantees the quote is in the prose.
 * christo composes `description` from the finding's own observation and
 * required_action (`warholReview.ts`), and the claim appears there only because
 * warhol's brief asks the verifier to name a rule by what it SAYS. So an
 * unmatched citation is an ordinary outcome, not a failure -- the comment
 * renders exactly as it does today and the chip remains the fallback.
 */

import { visit } from "unist-util-visit";

/** Custom element the Markdown component maps to the link renderer. */
export const CLAIM_CITATION_TAG = "claim-citation";

/** The minimum a claim must be before matching it is meaningful rather than noisy. */
export const MIN_LINKABLE_CHARS = 12;

/** A quote longer than this is a passage the prose paraphrases, not a wording it prints. */
const MAX_QUOTED_WORDING = 120;

export interface LinkableClaim {
  /** The registry rule, and the link's target. */
  ruleId: string;
  /** The source the rule was read from, carried so the viewer can open it. */
  sourceId?: string;
  /** What a deep link calls the cited citation: a cell range, or a page. */
  cite?: string;
  /**
   * 1-based position in the message's citation list.
   *
   * The chips under a message are numbered by that same position
   * (`ChatMessage.tsx`: `const n = index + 1`). Carrying it here is what lets
   * an inline link and a chip say "these are the same source" -- without it a
   * reader sees twelve numbered chips and three underlined phrases with no
   * way to tell which chip is which phrase.
   */
  index?: number;
  /** The claim as the registry holds it. Preferred over `quote` for matching. */
  statement?: string;
  /** The verbatim source cell, used when there is no statement. */
  quote?: string;
  /** The rule's brand scope, to tell identical wordings apart. */
  brand?: string;
  /** The rule's verdict (approved / prohibited / pending ...), likewise. */
  polarity?: string;
  /**
   * The prior chat that discussed this claim, when the graph recorded one.
   *
   * A claim can be linkable for two different reasons, and a reader needs to
   * know which: the registry GOVERNS it, or this enterprise already TALKED
   * about it. When this is set the link opens that conversation instead of the
   * registry row, and renders in the provenance colour.
   */
  chatId?: string;
  /** That conversation's title, for the link's tooltip. */
  chatLabel?: string;
  /** The memory that recorded this claim. Independent of `chatId`: a memory
   * and the conversation it came up in routinely land on the same claim, and
   * both are worth offering. */
  memoryId?: string;
  /** The memory's opening words, for the link's tooltip and its search. */
  memoryLabel?: string;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The comparable form of a claim and of the prose that may contain it.
 *
 * Only characters that never change WHICH claim is meant are folded, and the
 * folding must be LENGTH-PRESERVING: the match is mapped back onto the original
 * text to build the link, so an index in the folded string has to be the same
 * index in the original. That rules out collapsing whitespace here, which is
 * why the needle is matched with a whitespace-tolerant pattern instead.
 *
 * why the case fold is per code point and not one `.toLowerCase()` on the whole
 * string: lowercasing is not length-preserving in general. U+0130 (the Turkish
 * dotted capital I, ordinary in consumer-brand artwork copy) lowercases to `i` +
 * U+0307 -- two UTF-16 units out of one -- and a single one of those anywhere
 * before or inside a match shifts every later offset, so the plugin would slice
 * the ORIGINAL text at the wrong index and link the wrong characters. Folding
 * character by character and keeping the original unit whenever the lowercase
 * form changes width makes the invariant true rather than merely asserted; such
 * a character simply does not case-fold, which at worst misses a match.
 * `for...of` walks code points, so an astral character keeps its 2-unit width
 * and the width check stays meaningful.
 */
function fold(value: string): string {
  const normalized = value.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
  let out = "";
  for (const ch of normalized) {
    const lower = ch.toLowerCase();
    out += lower.length === ch.length ? lower : ch;
  }
  return out;
}

/**
 * A pattern that finds `text` however the prose spaced or punctuated it.
 *
 * Runs of whitespace match any run, because a claim broken across a line in the
 * source is one space in the prose; and the trailing `*` a registry keeps on a
 * qualified claim is optional, because the pack does not always print it.
 */
function needlePattern(text: string): RegExp {
  const body = text
    .trim()
    .replace(/[*\s]+$/, "")
    .split(/\s+/)
    .map(escapeRegExp)
    .join("\\s+");
  return new RegExp(`${body}\\*?`, "g");
}

/** One wording, and every cited row that prints it. */
interface Wording {
  key: string;
  claims: LinkableClaim[];
}

/** Case, curly quotes, spacing and a trailing `*` folded away: the wording itself. */
function wordingKey(text: string): string {
  return fold(text)
    .replace(/[*\s]+$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * The cited wordings, longest first, each with EVERY row that prints it.
 *
 * why rows are kept together rather than deduped: the same wording is a
 * separate registry row per brand and verdict -- "High protein" approved for one
 * SKU set, "High  Protein" unapproved for Activia -- and keeping only the first
 * row sent every occurrence to it (epic chat 6abc2cd0).
 */
function wordings(claims: LinkableClaim[]): Wording[] {
  const byKey = new Map<string, Wording>();
  for (const claim of claims) {
    if (!claim?.ruleId) continue;
    // why the quote too: a row whose statement carries a typo ("yout gut
    // wellbeing*") still quotes the wording the prose prints, and without it
    // that row's phrase linked a different row (epic chat 6abd3c41). A long
    // quote is a passage, not a wording, so it is left out.
    const texts = [claim.statement || claim.quote || ""];
    if (
      claim.statement &&
      claim.quote &&
      claim.quote.length <= MAX_QUOTED_WORDING
    )
      texts.push(claim.quote);
    for (const key of Array.from(new Set(texts.map(wordingKey)))) {
      if (key.length < MIN_LINKABLE_CHARS) continue;
      const found = byKey.get(key);
      if (!found) byKey.set(key, { key, claims: [claim] });
      else if (!found.claims.some((c) => c.ruleId === claim.ruleId))
        found.claims.push(claim);
    }
  }
  // Longest first: when one claim's wording contains another's, the specific
  // one must claim the span before the general one takes half of it.
  return Array.from(byKey.values()).sort((a, b) => b.key.length - a.key.length);
}

/** What a sentence says about a row's verdict, by the words a reader would use. */
const VERDICT_WORDS: Record<string, RegExp> = {
  approved: /(?<!not )\bapproved\b/,
  prohibited:
    /\b(unapproved|not approved|prohibited|not allowed|not permitted|banned)\b/,
  pending: /\bpending\b/,
  conditional: /\b(conditional|conditionally|provided|only if|only when)\b/,
};

/**
 * The verdict words a row's own verdict answers to.
 *
 * why pending and conditional together: a row from the "2. Pending Claims"
 * sheet is stored with polarity `conditional`, while the answer calls it
 * "pending" after its sheet -- so "is pending for Trinity" named no row and the
 * brand gave the phrase to the unapproved one (epic chat 6abd3c41).
 */
const ANSWERS_TO: Record<string, ReadonlySet<string>> = {
  approved: new Set(["approved"]),
  prohibited: new Set(["prohibited"]),
  pending: new Set(["pending", "conditional"]),
  conditional: new Set(["conditional", "pending"]),
};

function verdictOf(polarity: string | undefined): string | null {
  const value = (polarity || "").toLowerCase();
  if (!value) return null;
  if (value.includes("prohib") || value.includes("unapprov"))
    return "prohibited";
  if (value.includes("pending")) return "pending";
  if (value.includes("condition")) return "conditional";
  if (value.includes("approv")) return "approved";
  return null;
}

const BRAND_NOISE = new Set(["with", "and", "the", "for", "from", "free"]);

/** A sentence, folded, and where the wording sits in it. */
interface Reading {
  text: string;
  start: number;
  end: number;
}

/** The sentence around a match, folded, so its words can vote for a row. */
function sentenceAround(text: string, start: number, end: number): Reading {
  const before = text.slice(0, start);
  const from =
    Math.max(
      before.lastIndexOf("."),
      before.lastIndexOf(";"),
      before.lastIndexOf("!"),
      before.lastIndexOf("?"),
      before.lastIndexOf("\n"),
    ) + 1;
  const tail = text.slice(end).search(/[.;!?\n]/);
  return {
    text: fold(text.slice(from, tail < 0 ? text.length : end + tail)),
    start: start - from,
    end: end - from,
  };
}

/**
 * The verdict the words nearest the wording give, on either side; null when the
 * sentence names none.
 *
 * why nearest and not any: one sentence often names two rows' verdicts --
 * "“High protein” is approved for the peach SKUs, but “High Protein” is
 * unapproved", or "pending “X” to unapproved “X”" -- and a verdict anywhere in
 * it voted for both rows, so the brand decided and the links swapped (epic
 * chat 6abd3c41).
 */
function nearestVerdict(reading: Reading): string | null {
  let best: { verdict: string; distance: number } | null = null;
  for (const [verdict, pattern] of Object.entries(VERDICT_WORDS)) {
    const everywhere = new RegExp(pattern.source, "g");
    for (const match of Array.from(reading.text.matchAll(everywhere))) {
      const at = match.index ?? 0;
      const distance =
        at >= reading.end
          ? at - reading.end
          : Math.max(0, reading.start - (at + match[0].length));
      if (!best || distance < best.distance) best = { verdict, distance };
    }
  }
  return best?.verdict ?? null;
}

/**
 * How well a sentence names this row: its verdict (2), then its brand.
 *
 * A brand the sentence prints whole scores 1, plus a little per word so the
 * more specific brand wins; a brand it prints only in part scores under 1. The
 * verdict weighs more because the brand of a row is often the enterprise's own
 * everywhere in the answer.
 *
 * why whole-phrase over word share: on epic chat 6abc4db3 the "Activia Fibre"
 * table row linked the plain "Activia" row -- both approved, and "activia" plus
 * "fibre" (the claim's own word) gave each row every brand word it has.
 */
function score(claim: LinkableClaim, reading: Reading): number {
  const verdict = verdictOf(claim.polarity);
  const nearest = nearestVerdict(reading);
  const said =
    verdict !== null && nearest !== null && ANSWERS_TO[verdict].has(nearest);
  const sentence = reading.text;
  const brand = fold(claim.brand || "")
    .split("\n")[0]
    .replace(/\s+/g, " ")
    .trim();
  const words = brand
    .split(/[^a-z0-9%]+/)
    .filter((w) => w.length >= 3 && !BRAND_NOISE.has(w));
  if (!words.length) return said ? 2 : 0;
  const whole = sentence.replace(/\s+/g, " ").includes(brand);
  const share = words.filter((w) => sentence.includes(w)).length / words.length;
  return (said ? 2 : 0) + (whole ? 1 + 0.1 * words.length : 0.9 * share);
}

/** The row this sentence means; a tie keeps the panel's order. */
function bestFor(claims: LinkableClaim[], reading: Reading): LinkableClaim {
  let best = claims[0];
  let bestScore = score(best, reading);
  for (const claim of claims.slice(1)) {
    const value = score(claim, reading);
    const earlier =
      (claim.index ?? Number.MAX_SAFE_INTEGER) <
      (best.index ?? Number.MAX_SAFE_INTEGER);
    if (value > bestScore || (value === bestScore && earlier)) {
      best = claim;
      bestScore = value;
    }
  }
  return best;
}

interface Hit {
  start: number;
  end: number;
  claim: LinkableClaim;
  /** A `[n]` marker rather than the claim's wording: drawn as the number only. */
  marker?: boolean;
  /** A marker whose number a wording link in the same block already shows: drawn as nothing. */
  repeat?: boolean;
}

/** A citation marker in the prose: `[3]`, never a markdown link `[3](url)`. */
const MARKER_PATTERN = /\[(\d{1,3})\](?!\()/g;

/**
 * Where each `[n]` in `text` names a cited rule, by its chip number.
 *
 * why: a regulation rule is paraphrased, never quoted, so the wording pass has
 * nothing to link; warhol marks the sentence that states it with the rule's
 * chip number instead (`mark_stated`). Drawn as plain text, "[3]" named a chip
 * the reader then had to find (epic chat 6abc33fa). A number no rule chip
 * carries stays text.
 *
 * Exported for the tests.
 */
export function findMarkerHits(text: string, claims: LinkableClaim[]): Hit[] {
  const byIndex = new Map<number, LinkableClaim>();
  for (const claim of claims) {
    if (claim?.ruleId && claim.index) byIndex.set(claim.index, claim);
  }
  const hits: Hit[] = [];
  for (const match of Array.from(text.matchAll(MARKER_PATTERN))) {
    const claim = byIndex.get(Number(match[1]));
    if (!claim || match.index === undefined) continue;
    hits.push({
      start: match.index,
      end: match.index + match[0].length,
      claim,
      marker: true,
    });
  }
  return hits;
}

/**
 * The block a text node sits in, as the text a reader sees there.
 *
 * why not the text node alone: a node is often a fragment -- "unapproved" in
 * `**unapproved**` is its own node, and in a table each cell is. On epic chat
 * 6abc33fa both "High protein" cells of a table linked row 1, because the
 * verdict sat in the next cell and neither cell could see it.
 */
export interface BlockContext {
  /** The block's whole text, cells separated by " | ". */
  text: string;
  /** Where the text node starts inside `text`. */
  offset: number;
  /** A table row: every cell is one statement about one claim. */
  row: boolean;
  /** The block element itself: every text node of one block shares it. */
  block: unknown;
}

/** Blocks a sentence cannot run across. A cell is read through its row. */
const BLOCK_TAGS = new Set([
  "p",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "dt",
  "dd",
  "caption",
  "tr",
]);

/** Every text node inside a block, mapped to that block's context. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function blockContexts(tree: any): Map<unknown, BlockContext> {
  const contexts = new Map<unknown, BlockContext>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  visit(tree, "element", (block: any) => {
    if (!BLOCK_TAGS.has(block.tagName)) return;
    const row = block.tagName === "tr";
    const nodes: { node: unknown; offset: number }[] = [];
    let text = "";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const walk = (node: any) => {
      if (node.type === "text") {
        nodes.push({ node, offset: text.length });
        text += String(node.value ?? "");
        return;
      }
      if (row && (node.tagName === "td" || node.tagName === "th"))
        text += " | ";
      for (const child of node.children ?? []) walk(child);
    };
    walk(block);
    // Visited outside-in, so a nested block (a `p` in an `li`) overwrites
    // its parent's context with the tighter one.
    for (const { node, offset } of nodes)
      contexts.set(node, { text, offset, row, block });
  });
  return contexts;
}

/**
 * Where each cited row sits in `text`, without overlaps.
 *
 * A wording one row prints links its first occurrence, as before. A wording
 * several rows print links each occurrence, in reading order, to the unlinked
 * row the sentence around it names by verdict and brand; each row is linked at
 * most once. `context`, when given, is the block `text` sits in: the sentence
 * is read from the block, and a table row is read whole.
 *
 * Exported for the tests: the matching is the risky half of this feature, and
 * it is worth pinning without a renderer in the way.
 */
export function findClaimHits(
  text: string,
  claims: LinkableClaim[],
  context?: BlockContext,
): Hit[] {
  const haystack = fold(text);
  const hits: Hit[] = [];
  for (const { key, claims: rows } of wordings(claims)) {
    const pattern = needlePattern(key);
    const matches: { start: number; end: number }[] = [];
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(haystack)) !== null) {
      if (match[0].length === 0) break;
      matches.push({ start: match.index, end: match.index + match[0].length });
    }
    const left = [...rows];
    for (let i = 0; i < matches.length && left.length; i++) {
      const { start, end } = matches[i];
      // Never inside another claim's link.
      if (hits.some((hit) => start < hit.end && end > hit.start)) continue;
      const reading = !context
        ? sentenceAround(text, start, end)
        : context.row
          ? {
              text: fold(context.text),
              start: context.offset + start,
              end: context.offset + end,
            }
          : sentenceAround(
              context.text,
              context.offset + start,
              context.offset + end,
            );
      const claim = bestFor(left, reading);
      left.splice(left.indexOf(claim), 1);
      hits.push({ start, end, claim });
    }
  }
  return hits.sort((a, b) => a.start - b.start);
}

export interface ClaimCitationOptions {
  claims: LinkableClaim[];
  /**
   * Disambiguator for the rendered testids, normally the id of the comment or
   * message this body belongs to. A review renders one body per comment in a
   * single pass, so two findings resting on the SAME registry rule would
   * otherwise emit the same `claim-citation-link-<ruleId>` twice on one page --
   * a Playwright strict-mode violation and a breach of the testid-uniqueness
   * rule. Optional: a body rendered alone needs no suffix.
   */
  scope?: string;
}

/** 1 for a rule's first link in a body, 2 for the next, and so on. */
function nextOccurrence(seen: Map<string, number>, ruleId: string): number {
  const next = (seen.get(ruleId) ?? 0) + 1;
  seen.set(ruleId, next);
  return next;
}

export default function rehypeClaimCitation(options: ClaimCitationOptions) {
  const claims = options?.claims ?? [];
  const scope = options?.scope ?? "";
  return (tree: unknown) => {
    if (claims.length === 0) return;
    // How many times each rule has been linked in THIS body. `scope` keeps two
    // comments apart; this keeps two mentions inside one comment apart, which
    // the prose can legitimately contain. Counted here because this is where
    // the duplication is created -- the renderer only sees one hit at a time.
    // The first stays bare so the common single-mention id reads as just the
    // rule id.
    const seen = new Map<string, number>();
    const contexts = blockContexts(tree);
    // The chip numbers each block's wording links already show. why: warhol
    // marks the rules a source note names, and in a table row the wording link
    // carries the same number -- "High Protein3" then "3" (epic chat 6abd29ee).
    // A marker naming a DIFFERENT chip than the wording link still shows.
    const shown = new Map<unknown, Set<number>>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    visit(tree as any, "text", function (node: any, index, parent: any) {
      if (!parent || index === null || index === undefined) return;
      // Never link inside an existing anchor, and never re-enter our own output.
      if (parent.tagName === "a" || parent.tagName === CLAIM_CITATION_TAG)
        return;
      if (node.data?.processedByClaimCitation) return;

      const text: string = typeof node.value === "string" ? node.value : "";
      const context = contexts.get(node);
      const wording = findClaimHits(text, claims, context);
      const numbers = new Set<number>(
        context ? (shown.get(context.block) ?? []) : [],
      );
      for (const hit of wording) {
        if (hit.claim.index) numbers.add(hit.claim.index);
      }
      if (context) shown.set(context.block, numbers);
      const hits = [
        ...wording,
        ...findMarkerHits(text, claims)
          .filter(
            (m) => !wording.some((w) => m.start < w.end && m.end > w.start),
          )
          .map((m) =>
            m.claim.index && numbers.has(m.claim.index)
              ? { ...m, repeat: true }
              : m,
          ),
      ].sort((a, b) => a.start - b.start);
      if (hits.length === 0) return;

      const children: unknown[] = [];
      let cursor = 0;
      for (const hit of hits) {
        // A repeated marker goes with the space before it: "health [1]" reads "health".
        const before = hit.repeat
          ? text.slice(cursor, hit.start).replace(/ $/, "")
          : text.slice(cursor, hit.start);
        if (before) {
          children.push({
            type: "text",
            value: before,
            data: { processedByClaimCitation: true },
          });
        }
        if (hit.repeat) {
          cursor = hit.end;
          continue;
        }
        children.push({
          type: "element",
          tagName: CLAIM_CITATION_TAG,
          properties: {
            ruleId: hit.claim.ruleId,
            sourceId: hit.claim.sourceId ?? "",
            cite: hit.claim.cite ?? "",
            // Provenance rides along so the renderer can choose the target and
            // the colour without re-deriving it from the citation list.
            chatId: hit.claim.chatId ?? "",
            chatLabel: hit.claim.chatLabel ?? "",
            memoryId: hit.claim.memoryId ?? "",
            memoryLabel: hit.claim.memoryLabel ?? "",
            index: hit.claim.index ?? 0,
            scope,
            occurrence: nextOccurrence(seen, hit.claim.ruleId),
            // A marker is the number alone: "[3]" becomes the linked superscript 3.
            marker: hit.marker ? true : undefined,
            label: hit.marker
              ? hit.claim.statement || hit.claim.quote || ""
              : undefined,
          },
          children: hit.marker
            ? []
            : [
                {
                  type: "text",
                  // The pack's own wording, not the registry's: the sentence
                  // must read as it was written.
                  value: text.slice(hit.start, hit.end),
                  data: { processedByClaimCitation: true },
                },
              ],
        });
        cursor = hit.end;
      }
      if (cursor < text.length) {
        children.push({
          type: "text",
          value: text.slice(cursor),
          data: { processedByClaimCitation: true },
        });
      }
      parent.children.splice(index, 1, ...children);
      // Skip the nodes just inserted, or the visitor walks into them.
      return index + children.length;
    });
  };
}
